import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, finalize } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';
import { WebService } from '../../shared/services/web.service';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  // null = refreshing in progress; '' = refresh failed; string = new access token
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  private readonly skipAuthUrls = ['adminLogin', 'getRefreshToken'];

  constructor(
    private authenticationService: AuthenticationService,
    private webservice: WebService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const shouldSkipAuth = this.skipAuthUrls.some((url) => request.url.includes(url));

    let currentUser = this.authenticationService.currentUserValue;
    if (!currentUser?.token) {
      try {
        currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
        if (currentUser?.token) {
          this.authenticationService.setCurrentUser(currentUser);
        }
      } catch {
        currentUser = null;
      }
    }

    if (!shouldSkipAuth && currentUser?.token) {
      request = this.addToken(request, currentUser.token);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (shouldSkipAuth) {
          return throwError(() => error);
        }

        const errorBody = this.parseErrorBody(error);
        if (errorBody?.Message === 'Invalid admin credentials.') {
          return throwError(() => error);
        }

        // Tampered/missing token — clear session immediately
        if (this.isInvalidOrMissingToken(errorBody)) {
          this.forceLogout();
          return throwError(() => error);
        }

        // Expired access token — try refresh, then logout if that fails
        if (error.status === 401 || this.isTokenExpired(errorBody)) {
          return this.handle401Error(request, next);
        }

        return throwError(() => error);
      })
    );
  }

  private parseErrorBody(error: HttpErrorResponse): any {
    try {
      if (!error?.error) {
        return null;
      }
      if (typeof error.error === 'string') {
        return JSON.parse(error.error);
      }
      return error.error;
    } catch {
      return null;
    }
  }

  private getErrorText(errorBody: any): string {
    if (!errorBody) {
      return '';
    }
    return String(
      errorBody.data ?? errorBody.message ?? errorBody.Message ?? errorBody.error ?? ''
    ).toLowerCase();
  }

  private isInvalidOrMissingToken(errorBody: any): boolean {
    const message = this.getErrorText(errorBody);
    return (
      message.includes('invalid token') ||
      message.includes('auth token missing') ||
      message.includes('token missing')
    );
  }

  private isTokenExpired(errorBody: any): boolean {
    return this.getErrorText(errorBody).includes('token expired');
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private getStoredUser(): any {
    let currentUser = this.authenticationService.currentUserValue;
    if (!currentUser?.refreshtoken) {
      try {
        currentUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      } catch {
        currentUser = null;
      }
    }
    return currentUser;
  }

  private forceLogout(): void {
    this.isRefreshing = false;
    // Unblock any requests waiting for a refresh result
    this.refreshTokenSubject.next('');
    this.authenticationService.logout();
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const currentUser = this.getStoredUser();

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      if (!currentUser?.refreshtoken) {
        this.forceLogout();
        return throwError(() => new Error('Refresh token is not available.'));
      }

      const payload = { refreshtoken: currentUser.refreshtoken };
      return this.webservice.requestJson('getRefreshToken', payload).pipe(
        switchMap((data) => {
          const response = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
          if (!response?.accessToken) {
            this.forceLogout();
            return throwError(() => new Error('Failed to refresh access token.'));
          }

          currentUser.token = response.accessToken;
          this.authenticationService.setCurrentUser(currentUser);
          this.refreshTokenSubject.next(response.accessToken);
          return next.handle(this.addToken(request, response.accessToken));
        }),
        catchError((error) => {
          this.forceLogout();
          return throwError(() => error);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('Session expired. Please log in again.'));
        }
        return next.handle(this.addToken(request, token));
      })
    );
  }
}
